import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  listVendorAppointments,
  listVendorAppointmentFilterOptions,
  listActiveIndividuals,
  listActiveSubs,
  createVendorAppointment,
} from "./appointments.api";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { ReadAllResponse } from "@/interfaces";
import { appointment_status } from "@/models/appointment.model";
import { CreateAppointmentPayload } from "@/app/pmc/appointments/appointments.entity.ts";

interface FilterState {
  status: string;
  serviceParent: string;
  assignee: string;
  search: string;
  dateFrom: string;
  dateTo: string;
  sortKey: string;
  sortOrder: "asc" | "desc";
}

interface PaginationState {
  page: number;
  pageSize: number;
}

interface VendorAppointmentFilterOptions {
  serviceParents: string[];
  assigneeNames: string[];
}

interface Individual {
  id: string;
  name: string;
}

interface Sub {
  id: string;
  name: string;
}

interface VendorAppointmentsState {
  list: AppointmentJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: FilterState;
  pagination: PaginationState;
  options: VendorAppointmentFilterOptions;
  individuals: Individual[];
  subs: Sub[];
  subsLoading: boolean;
  sheetOpen: boolean;
  creating: boolean;
}

const initialState: VendorAppointmentsState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    status: "",
    serviceParent: "",
    assignee: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortKey: "created_at",
    sortOrder: "desc",
  },
  pagination: { page: 1, pageSize: 50 },
  options: { serviceParents: [], assigneeNames: [] },
  individuals: [],
  subs: [],
  subsLoading: false,
  sheetOpen: false,
  creating: false,
};

export const fetchVendorAppointments = createAsyncThunk(
  "vendorAppointments/fetch",
  async (searchOverride?: string, { getState, signal } = {} as any) => {
  const s = (getState() as { vendorAppointments: VendorAppointmentsState }).vendorAppointments;
  const { page, pageSize } = s.pagination;
    const { status, serviceParent, assignee, search, dateFrom, dateTo, sortKey, sortOrder } = s.filters;

  // Check if the request was cancelled
  if (signal.aborted) {
    throw new Error("Request cancelled");
  }

  return await listVendorAppointments(
    page,
    pageSize,
    {
      status: status ? (status as appointment_status) : undefined,
      serviceParent: serviceParent || undefined,
      assignee: assignee || undefined,
        search: searchOverride !== undefined ? searchOverride : (search && search.trim() ? search.trim() : undefined),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
    },
    sortKey,
    sortOrder
  );
  }
);

export const fetchVendorAppointmentFilterOptions = createAsyncThunk(
  "vendorAppointments/fetchFilterOptions",
  async () => await listVendorAppointmentFilterOptions()
);

export const fetchActiveIndividuals = createAsyncThunk(
  "vendorAppointments/fetchActiveIndividuals",
  async () => await listActiveIndividuals()
);

export const fetchActiveSubs = createAsyncThunk(
  "vendorAppointments/fetchActiveSubs",
  async () => await listActiveSubs()
);

export const createAppointment = createAsyncThunk(
  "vendorAppointments/create",
  async (payload: Omit<CreateAppointmentPayload, "vendor">) => {
    return await createVendorAppointment(payload);
  }
);

const slice = createSlice({
  name: "vendorAppointments",
  initialState,
  reducers: {
    /* filter setters */
    setStatus(s, a: PayloadAction<string>) {
      s.filters.status = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setServiceParent(s, a: PayloadAction<string>) {
      s.filters.serviceParent = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setAssignee(s, a: PayloadAction<string>) {
      s.filters.assignee = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setSearch(s, a: PayloadAction<string>) {
      s.filters.search = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setDateFrom(s, a: PayloadAction<string>) {
      s.filters.dateFrom = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setDateTo(s, a: PayloadAction<string>) {
      s.filters.dateTo = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },

    /* pagination */
    setPage(s, a: PayloadAction<number>) {
      s.pagination.page = a.payload;
    },
    setPageSize(s, a: PayloadAction<number>) {
      s.pagination.pageSize = a.payload;
    },

    /* sorting */
    setSortKey(s, a: PayloadAction<string>) {
      if (s.filters.sortKey === a.payload) {
        s.filters.sortOrder = s.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        s.filters.sortKey = a.payload;
        s.filters.sortOrder = "asc";
      }
    },

    /* appointment updates */
    updateAppointmentInList(s, a: PayloadAction<AppointmentJoin>) {
      const idx = s.list.findIndex((appointment) => appointment.id === a.payload.id);
      if (idx >= 0) {
        s.list[idx] = a.payload;
      }
    },

    /* sheet state */
    openSheet(s) {
      s.sheetOpen = true;
    },
    closeSheet(s) {
      s.sheetOpen = false;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchVendorAppointments.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchVendorAppointments.fulfilled, (s, a: PayloadAction<ReadAllResponse<AppointmentJoin>>) => {
      s.loading = false;
      s.list = a.payload.data;
      s.total = a.payload.count ?? 0;
    });
    b.addCase(fetchVendorAppointments.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message || "Failed to fetch appointments.";
    });

    b.addCase(fetchVendorAppointmentFilterOptions.fulfilled, (s, a: PayloadAction<VendorAppointmentFilterOptions>) => {
      s.options = a.payload;
    });

    b.addCase(fetchActiveIndividuals.fulfilled, (s, a) => {
      s.individuals = a.payload as any;
    });

    // Fetch subs
    b.addCase(fetchActiveSubs.pending, (s) => {
      s.subsLoading = true;
    });
    b.addCase(fetchActiveSubs.fulfilled, (s, a) => {
      s.subsLoading = false;
      s.subs = a.payload as any;
    });
    b.addCase(fetchActiveSubs.rejected, (s) => {
      s.subsLoading = false;
    });

    // Create appointment
    b.addCase(createAppointment.pending, (s) => {
      s.creating = true;
    });
    b.addCase(createAppointment.fulfilled, (s, a) => {
      s.creating = false;
      // Optionally add the new appointment to the list
      // s.list.unshift(a.payload);
    });
    b.addCase(createAppointment.rejected, (s) => {
      s.creating = false;
    });
  },
});

export const {
  setStatus,
  setServiceParent,
  setAssignee,
  setSearch,
  setDateFrom,
  setDateTo,
  setPage,
  setPageSize,
  setSortKey,
  updateAppointmentInList,
  openSheet,
  closeSheet,
} = slice.actions;

export default slice.reducer;
